import { useEffect, useRef } from "react";
import { updatePresence, removePresence } from "../lib/firebase";

// Human-readable labels for each route
const PAGE_LABELS = {
  "/": "Home",
  "/our-story": "Our Story",
  "/gallery": "Gallery",
  "/upload": "Uploading Photos",
  "/selfie": "Taking a Selfie",
  "/guestbook": "Guest Book",
  "/videos": "Videos",
  "/guest-photos": "Guest Photos",
  "/dj-requests": "DJ Requests",
  "/venue": "Venue Info",
  "/our-journey": "Our Journey",
  "/amor": "Amor",
  "/bella-onboarding": "Getting Started",
  "/admin": "Admin Dashboard",
};

export function usePresence({ name, role, page }) {
  const sessionIdRef = useRef(null);
  const intervalRef = useRef(null);
  const nameRef = useRef(name);
  const roleRef = useRef(role);
  const pageRef = useRef(page);

  // Keep refs in sync so the interval always uses latest values
  useEffect(() => { nameRef.current = name; }, [name]);
  useEffect(() => { roleRef.current = role; }, [role]);
  useEffect(() => { pageRef.current = page; }, [page]);

  // Generate a stable session ID on first mount
  useEffect(() => {
    if (!sessionIdRef.current) {
      sessionIdRef.current = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    }
  }, []);

  // Write presence whenever name/role/page changes, and on an interval
  useEffect(() => {
    if (!name || !role) return; // not logged in yet

    const sessionId = sessionIdRef.current;

    const write = () => {
      const pageLabel = PAGE_LABELS[pageRef.current] || pageRef.current || "App";
      updatePresence(sessionId, {
        name: nameRef.current || "Guest",
        role: roleRef.current,
        page: pageRef.current,
        pageLabel,
      }).catch(() => {});
    };

    write(); // immediate write on change

    // Heartbeat every 30 seconds
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(write, 30000);

    // Cleanup: remove presence on unmount / logout / tab close
    const cleanup = () => {
      if (sessionIdRef.current) {
        removePresence(sessionIdRef.current).catch(() => {});
      }
    };

    window.addEventListener("beforeunload", cleanup);
    window.addEventListener("pagehide", cleanup);

    return () => {
      clearInterval(intervalRef.current);
      window.removeEventListener("beforeunload", cleanup);
      window.removeEventListener("pagehide", cleanup);
      cleanup();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, role]);

  // Also write immediately when page changes (without restarting the interval)
  useEffect(() => {
    if (!name || !role || !sessionIdRef.current) return;
    const pageLabel = PAGE_LABELS[page] || page || "App";
    updatePresence(sessionIdRef.current, {
      name: name || "Guest",
      role,
      page,
      pageLabel,
    }).catch(() => {});
  }, [page, name, role]);
}
